import { ArrowLeft, Check, Clock, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import SEO from "../components/site/SEO";
import Reveal from "../components/Reveal";
import { LocalizedLink } from "../lib/i18nRouting";
import { HOLIDAYS_PATH, TERMS_PATH } from "../data/holidays";

export default function BookingTermsPage() {
  const { t } = useTranslation();
  const sections = t("holidays.terms.sections", { returnObjects: true });
  return (
    <div className="bg-bone" data-testid="booking-terms-page">
      <SEO titleKey="terms.title" descKey="terms.description" path={TERMS_PATH} />

      <section className="bg-forest text-bone pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12">
          <Reveal>
            <LocalizedLink to={HOLIDAYS_PATH} data-testid="terms-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider-3 text-bone/70 link-underline">
              <ArrowLeft className="w-4 h-4" /> {t("holidays.terms.back")}
            </LocalizedLink>
          </Reveal>
          <Reveal delay={80} className="mt-10 text-xs uppercase tracking-wider-3 text-bone/60">{t("holidays.terms.eyebrow")}</Reveal>
          <Reveal delay={140}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] mt-4">
              {t("holidays.terms.title_a")} <span className="italic">{t("holidays.terms.title_b")}</span>
            </h1>
          </Reveal>
          <Reveal delay={200} className="mt-6 text-base md:text-lg text-bone/80 leading-relaxed max-w-2xl">{t("holidays.terms.intro")}</Reveal>
          <Reveal delay={240} className="mt-6 text-[10px] uppercase tracking-wider-3 text-bone/50">{t("holidays.terms.updated")}</Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 space-y-10">
          {sections.map((s, i) => (
            <Reveal key={i} className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 border-t border-bone-deep pt-8" data-testid={`terms-section-${i}`}>
              <div className="md:col-span-4">
                <div className="text-[10px] uppercase tracking-wider-3 text-moss">{String(i + 1).padStart(2, "0")}</div>
                <h2 className="font-display text-2xl lg:text-3xl text-forest leading-tight mt-1">{s.title}</h2>
              </div>
              <ul className="md:col-span-8 space-y-3">
                {s.items.map((it, j) => (
                  <li key={j} className="flex items-start gap-3 text-sm md:text-base text-foreground leading-relaxed">
                    <Check className="w-4 h-4 text-terracotta shrink-0 mt-1" /> <span>{it}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}

          <Reveal className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 border-t border-bone-deep pt-8" data-testid="terms-pending">
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-wider-3 text-moss">{String(sections.length + 1).padStart(2, "0")}</div>
              <h2 className="font-display text-2xl lg:text-3xl text-forest leading-tight mt-1">{t("holidays.terms.pending_title")}</h2>
            </div>
            <div className="md:col-span-8 bg-white border border-bone-deep p-6">
              <span className="inline-flex items-center gap-2 border border-terracotta text-terracotta text-[10px] uppercase tracking-wider-3 px-3 py-1.5">
                <Clock className="w-3 h-3" /> {t("holidays.terms.pending_badge")}
              </span>
              <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">{t("holidays.terms.pending_text")}</p>
            </div>
          </Reveal>

          <Reveal className="border-t border-bone-deep pt-8 flex items-start gap-3 text-sm text-muted-foreground">
            <Mail className="w-4 h-4 text-terracotta shrink-0 mt-0.5" /> {t("holidays.terms.contact")}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
